import {
  Button,
  FieldWrapper,
  Icons,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country } from '@prisma/client';
import { KeyedMutator } from 'swr';
import { useForm } from '../../../../hooks/useZodForm';
import { CompanyNameInput } from '../../common';
import { TUAEIncorporationRequestData } from '../types';
import { entityNameSchema, TEntityNameFields } from './schema';

type TEntityNameProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  next: () => void;
  back: () => void;
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
};

export const EntityNames = ({
  workRequest,
  next,
  mutate,
  back,
  setNotification,
}: TEntityNameProps) => {
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<TEntityNameFields>({
    resolver: zodResolver(entityNameSchema),
    defaultValues: {
      corporationName: workRequest.requestData?.companyLegalName,
      companyLegalName2: workRequest.requestData?.companyLegalName2,
      companyLegalName3: workRequest.requestData?.companyLegalName3,
      translatedCorporationName:
        workRequest.requestData?.translatedCorporationName,
      translatedCompanyLegalName2:
        workRequest.requestData?.translatedCompanyLegalName2,
      translatedCompanyLegalName3:
        workRequest.requestData?.translatedCompanyLegalName3,
      hasTranslation: !!(
        workRequest.requestData?.translatedCorporationName &&
        workRequest.requestData?.translatedCompanyLegalName2 &&
        workRequest.requestData?.translatedCompanyLegalName3
      ),
    },
  });

  const onSubmit = async (formData: TEntityNameFields) => {
    const res = await fetcher(
      `workRequest/${workRequest.id}`,
      {
        method: 'POST',
        body: {
          requestData: {
            ...(workRequest.requestData || {}),
            companyLegalName: formData.corporationName,
            companyLegalName2: formData.companyLegalName2,
            companyLegalName3: formData.companyLegalName3,
            ...(!formData.hasTranslation
              ? {
                  translatedCorporationName: null,
                  translatedCompanyLegalName2: null,
                  translatedCompanyLegalName3: null,
                }
              : {
                  translatedCorporationName: formData.translatedCorporationName,
                  translatedCompanyLegalName2:
                    formData.translatedCompanyLegalName2,
                  translatedCompanyLegalName3:
                    formData.translatedCompanyLegalName3,
                }),
          },
        },
      },
      undefined
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }
    await mutate();
    next();
  };

  return (
    <div className="flex flex-col gap-10">
      <h2 className="txt-large-bold">Entity Names</h2>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          What do you want to name your entity?
        </Label>
        <p className="txt-small-regular">
          Please submit three name options. If none of your choices are
          approved, we may ask for more options.
        </p>
        <div className="mt-2 flex flex-col gap-5">
          <CompanyNameInput
            label="Option 1"
            labelClassname="txt-small-medium"
            onValueChange={(val) => {
              setValue('corporationName', val);
              clearErrors('corporationName');
            }}
            value={watch('corporationName')}
            error={errors?.corporationName?.message}
            nameValidationOptions={{
              country: Country.AE,
              companyDescription: '',
              entityType: '',
              industry: '',
            }}
          />
          <CompanyNameInput
            label="Option 2"
            labelClassname="txt-small-medium"
            onValueChange={(val) => {
              setValue('companyLegalName2', val);
              clearErrors('companyLegalName2');
            }}
            value={watch('companyLegalName2')}
            error={errors?.companyLegalName2?.message}
            nameValidationOptions={{
              country: Country.AE,
              companyDescription: '',
              entityType: '',
              industry: '',
            }}
          />
          <CompanyNameInput
            label="Option 3 "
            labelClassname="txt-small-medium"
            onValueChange={(val) => {
              setValue('companyLegalName3', val);
              clearErrors('companyLegalName3');
            }}
            value={watch('companyLegalName3')}
            error={errors?.companyLegalName3?.message}
            nameValidationOptions={{
              country: Country.AE,
              companyDescription: '',
              entityType: '',
              industry: '',
            }}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="flex flex-col gap-2">
        <Label className="txt-medium-bold">
          Do you have a preferred Arabic translation for your name choice?
        </Label>
        <div className="mt-2">
          <RadioGroup
            className="flex flex-col gap-5"
            value={watch('hasTranslation') ? 'yes' : 'no'}
            onValueChange={(val) => setValue('hasTranslation', val === 'yes')}
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem id="has-translation-yes" value="yes" />
              <Label htmlFor="has-translation-yes">Yes</Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem id="has-translation-no" value="no" />
              <Label htmlFor="has-translation-no">No</Label>
            </div>
          </RadioGroup>
        </div>
      </FieldWrapper>

      {watch('hasTranslation') ? (
        <FieldWrapper _className="flex flex-col gap-2">
          <Label className="txt-medium-bold">
            Enter your translations here
          </Label>
          <div className="mt-2 flex flex-col gap-5">
            <CompanyNameInput
              label="Option 1"
              labelClassname="txt-small-medium"
              inputClassname="text-right"
              onValueChange={(val) => {
                setValue('translatedCorporationName', val);
                clearErrors('translatedCorporationName');
              }}
              value={watch('translatedCorporationName')}
              error={errors?.translatedCorporationName?.message}
              nameValidationOptions={{
                country: Country.AE,
                companyDescription: '',
                entityType: '',
                industry: '',
              }}
            />
            <CompanyNameInput
              label="Option 2"
              labelClassname="txt-small-medium"
              inputClassname="text-right"
              onValueChange={(val) => {
                setValue('translatedCompanyLegalName2', val);
                clearErrors('translatedCompanyLegalName2');
              }}
              value={watch('translatedCompanyLegalName2')}
              error={errors?.translatedCompanyLegalName2?.message}
              nameValidationOptions={{
                country: Country.AE,
                companyDescription: '',
                entityType: '',
                industry: '',
              }}
            />
            <CompanyNameInput
              label="Option 3 "
              labelClassname="txt-small-medium"
              inputClassname="text-right"
              onValueChange={(val) => {
                setValue('translatedCompanyLegalName3', val);
                clearErrors('translatedCompanyLegalName3');
              }}
              value={watch('translatedCompanyLegalName3')}
              error={errors?.translatedCompanyLegalName3?.message}
              nameValidationOptions={{
                country: Country.AE,
                companyDescription: '',
                entityType: '',
                industry: '',
              }}
            />
          </div>
        </FieldWrapper>
      ) : null}

      <div className="flex gap-4 items-center">
        <Button variant="secondary" onClick={back}>
          <Icons.ArrowLeft className="size-5" />
          <span>Back</span>
        </Button>
        <Button size="lg" onClick={handleSubmit(onSubmit)}>
          <span>Proceed</span>
          <Icons.ArrowRight className="size-5" />
        </Button>
      </div>
    </div>
  );
};
